import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  fetchCrmDashboard,
  isCrmDisabledError,
  isCrmViewBlockedError,
} from '../../api/crmCustomers';
import {
  formatDate,
  formatEnumLabel,
  formatValue,
} from '../../utils/crmFormatting';

const EMPTY_DASHBOARD = {
  today: '',
  dueWork: [],
  legacyDueBuckets: null,
  recentActivity: [],
};

const TENANT_WIDE_ROLES = new Set(['TENANT_ADMIN', 'SUPER_ADMIN']);

const ACTIVITY_TYPE_LABELS = {
  CALL: 'Call',
  VISIT: 'Visit',
  EMAIL: 'Email',
  ISSUE: 'Issue',
  INTERNAL_NOTE: 'Internal note',
  OTHER: 'Other',
};

function normalizeDateOnly(value) {
  const normalized = String(value ?? '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(normalized) ? normalized : '';
}

function getLocalTodayString() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function buildLegacyItemKey(item, bucketKey, index) {
  const normalizedId = String(item?.id ?? '').trim();

  if (normalizedId) {
    return normalizedId;
  }

  return [
    bucketKey,
    item?.customerNumber ?? '',
    item?.dueDate ?? '',
    item?.title ?? '',
    item?.assignedUser?.id ?? 'unassigned',
    index,
  ].join('|');
}

function collectLegacyBucketItems(data, bucketKeys) {
  const seen = new Set();

  return bucketKeys.flatMap((bucketKey) => {
    const items = Array.isArray(data?.[bucketKey]) ? data[bucketKey] : [];

    return items.filter((item, index) => {
      if (!item || typeof item !== 'object') {
        return false;
      }

      const itemKey = buildLegacyItemKey(item, bucketKey, index);

      if (seen.has(itemKey)) {
        return false;
      }

      seen.add(itemKey);
      return true;
    });
  });
}

function normalizeDashboard(data) {
  const recentActivity = Array.isArray(data?.recentActivity) ? data.recentActivity : [];

  if (Array.isArray(data?.dueWork)) {
    return {
      today: normalizeDateOnly(data?.today) || getLocalTodayString(),
      dueWork: data.dueWork.filter((item) => item && typeof item === 'object'),
      legacyDueBuckets: null,
      recentActivity,
    };
  }

  return {
    today: '',
    dueWork: [],
    legacyDueBuckets: {
      overdue: collectLegacyBucketItems(data, ['myOverdue', 'unassignedOverdue']),
      dueToday: collectLegacyBucketItems(data, ['myDueToday', 'unassignedDueToday']),
      planned: [],
    },
    recentActivity,
  };
}

function buildCustomerHref(customerNumber) {
  return `/customers/${encodeURIComponent(String(customerNumber ?? '').trim())}`;
}

function getActivityTypeLabel(activityType) {
  return ACTIVITY_TYPE_LABELS[activityType] || formatEnumLabel(activityType, 'Activity');
}

function getDueStatusModel(tone, dueDate) {
  const formattedDate = formatDate(dueDate, 'No due date');

  if (tone === 'overdue') {
    return {
      primaryLabel: 'Overdue',
      secondaryLabel: formattedDate,
      tone: 'overdue',
    };
  }

  if (tone === 'today') {
    return {
      primaryLabel: 'Due today',
      secondaryLabel: formattedDate,
      tone: 'today',
    };
  }

  if (formattedDate !== 'No due date') {
    return {
      primaryLabel: `Due ${formattedDate}`,
      secondaryLabel: '',
      tone: 'planned',
    };
  }

  return {
    primaryLabel: 'Due date unavailable',
    secondaryLabel: '',
    tone: 'planned',
  };
}

function buildTaskItem(item, tone) {
  const dueStatus = getDueStatusModel(tone, item?.dueDate);
  const contactName = String(item?.contact?.name ?? '').trim();
  const isUnassigned = item?.assignedUser == null;

  return {
    id: item?.id ?? `${tone}-${item?.customerNumber}-${item?.title}`,
    href: buildCustomerHref(item?.customerNumber),
    title: formatValue(item?.title, 'Untitled activity'),
    customerLabel: formatValue(item?.customerName, item?.customerNumber),
    notePreview: String(item?.note ?? '').trim(),
    activityTypeLabel: getActivityTypeLabel(item?.activityType),
    assigneeLabel: String(item?.assignedUser?.displayName ?? '').trim() || 'Unassigned',
    contactLabel: contactName ? `${contactName}${item?.contact?.active ? '' : ' (Archived)'}` : '',
    contactMuted: Boolean(item?.contact && item.contact.active === false),
    isUnassigned,
    primaryDueLabel: dueStatus.primaryLabel,
    secondaryDueLabel: dueStatus.secondaryLabel,
    tone: dueStatus.tone,
  };
}

function buildTaskSection({ emptyMessage, items, key, title, tone }) {
  return {
    key,
    title,
    tone,
    emptyMessage,
    items: items.map((item) => buildTaskItem(item, tone)),
  };
}

function groupDueWorkByDate(dashboard) {
  if (dashboard.legacyDueBuckets) {
    return {
      overdue: dashboard.legacyDueBuckets.overdue,
      dueToday: dashboard.legacyDueBuckets.dueToday,
      planned: dashboard.legacyDueBuckets.planned,
    };
  }

  const today = normalizeDateOnly(dashboard.today) || getLocalTodayString();
  const overdue = [];
  const dueToday = [];
  const planned = [];

  dashboard.dueWork.forEach((item) => {
    const dueDate = normalizeDateOnly(item?.dueDate);

    if (!dueDate) {
      return;
    }

    if (dueDate < today) {
      overdue.push(item);
      return;
    }

    if (dueDate === today) {
      dueToday.push(item);
      return;
    }

    planned.push(item);
  });

  return {
    overdue,
    dueToday,
    planned,
  };
}

function getOverdueSectionTitle(isTenantWideView) {
  return isTenantWideView ? 'Overdue activities' : 'Overdue';
}

function getDueTodaySectionTitle(isTenantWideView) {
  return isTenantWideView ? 'Activities due today' : 'Due today';
}

function getPlannedSectionTitle() {
  return 'Planned activities';
}

function getOverdueEmptyMessage(isTenantWideView) {
  return isTenantWideView
    ? 'No overdue activities.'
    : 'No overdue activities assigned to you.';
}

function getDueTodayEmptyMessage(isTenantWideView) {
  return isTenantWideView ? 'No activities due today.' : 'No activities due today for you.';
}

export default function useCrmDashboardController({
  connected,
  currentUser,
  setConnected,
  setError,
}) {
  const [dashboard, setDashboard] = useState(EMPTY_DASHBOARD);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const requestRef = useRef(0);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTenantWideView = TENANT_WIDE_ROLES.has(currentUser?.role);

  const loadDashboard = useCallback(async () => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setIsLoading(true);
    setLoadError('');

    try {
      const loadedDashboard = await fetchCrmDashboard();

      if (requestRef.current !== requestId) {
        return;
      }

      setDashboard(normalizeDashboard(loadedDashboard));
    } catch (error) {
      if (requestRef.current !== requestId) {
        return;
      }

      console.error('Failed to load CRM dashboard:', error);

      if (isCrmDisabledError(error)) {
        setError(error.message || 'CRM is disabled for this tenant');
        navigate('/', { replace: true });
        return;
      }

      if (isCrmViewBlockedError(error)) {
        setConnected(false);
        return;
      }

      setLoadError(error.message || 'Failed to load the CRM dashboard.');
    } finally {
      if (requestRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [navigate, setConnected, setError]);

  useEffect(() => {
    if (!connected) {
      requestRef.current += 1;
      setDashboard(EMPTY_DASHBOARD);
      setIsLoading(false);
      setLoadError('');
      return undefined;
    }

    loadDashboard();

    return () => {
      requestRef.current += 1;
    };
  }, [connected, loadDashboard]);

  const dueGroups = useMemo(() => groupDueWorkByDate(dashboard), [dashboard]);

  const counts = useMemo(
    () => ({
      overdue: dueGroups.overdue.length,
      dueToday: dueGroups.dueToday.length,
      planned: dueGroups.planned.length,
    }),
    [dueGroups]
  );

  const overdueSection = useMemo(
    () =>
      buildTaskSection({
        emptyMessage: getOverdueEmptyMessage(isTenantWideView),
        items: dueGroups.overdue,
        key: 'overdue',
        title: getOverdueSectionTitle(isTenantWideView),
        tone: 'overdue',
      }),
    [dueGroups.overdue, isTenantWideView]
  );

  const dueTodaySection = useMemo(
    () =>
      buildTaskSection({
        emptyMessage: getDueTodayEmptyMessage(isTenantWideView),
        items: dueGroups.dueToday,
        key: 'due-today',
        title: getDueTodaySectionTitle(isTenantWideView),
        tone: 'today',
      }),
    [dueGroups.dueToday, isTenantWideView]
  );

  const plannedSection = useMemo(
    () =>
      buildTaskSection({
        emptyMessage: 'No planned activities.',
        items: dueGroups.planned,
        key: 'planned',
        title: getPlannedSectionTitle(),
        tone: 'planned',
      }),
    [dueGroups.planned]
  );

  return {
    counts,
    dueTodaySection,
    hasLoadedData:
      counts.overdue > 0 ||
      counts.dueToday > 0 ||
      counts.planned > 0,
    isLoading,
    isMobile,
    isTenantWideView,
    loadError,
    onRefresh: loadDashboard,
    overdueSection,
    plannedSection,
  };
}
